import { useMemo } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  type DataExportTarget,
  type TimeFrame,
} from "@features/data-export/types";
import { DATE_RANGE_CHOICE_LABELS } from "@features/sales-v3/top-level-filters/components/date-filters";
import { maxLookbackDaysForTimeGranularity } from "@features/sales-v3/utils/max-lookback-days-for-time-granularity";
import {
  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY,
  dateRangeOptionsForTimeGranularity,
  type DateRangeChoiceV2,
  type DateRangeChoiceV3,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { DateRangePicker } from "@shared/date-range-picker";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { TextInput } from "@shared/text-input";

interface Props {
  target: DataExportTarget;
  timeGranularity: ITimeGranularityV2;
  timeFrame: TimeFrame;
  fileName: string;
  onTimeGranularityChange(value: ITimeGranularityV2): void;
  onTimeFrameChange(value: TimeFrame): void;
  onFileNameChange(value: string): void;
}

export const TimeFrameSelector = ({
  target,
  timeGranularity,
  timeFrame,
  fileName,
  onTimeGranularityChange,
  onTimeFrameChange,
  onFileNameChange,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const dateRangeOptions = useMemo(() => {
    const options = dateRangeOptionsForTimeGranularity(timeGranularity);

    if (timeGranularity === "daily") {
      options.push({ value: "todayAndYesterday" as any, label: "" });
    }

    return options.map(({ value }) => ({
      value,
      label: DATE_RANGE_CHOICE_LABELS[value],
    }));
  }, [timeGranularity]);

  const [, maxLookbackDays] =
    maxLookbackDaysForTimeGranularity(timeGranularity);

  return (
    <div className="bg-cosmic-4 pl-7 pr-7 pt-5 pb-5">
      <div>
        <h3 className="text-base font-semibold m-0 mb-3">Select time frame</h3>
        <div className="font-normal text-[0.65rem] text-gray-600 mb-1">
          Granularity
        </div>
        <TimeGranularityV2Select
          label=""
          icon={null}
          timeGranularity={timeGranularity}
          onChange={(value: ITimeGranularityV2) => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onChange",
              additionalActionInfo: "Set Time Granularity",
              data: {
                newTimeGranularity: value,
                pageContext: { timeGranularity },
              },
            });

            onTimeFrameChange(
              DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY[value],
            );
            onTimeGranularityChange(value);
          }}
        />
        <div className="font-normal text-[0.65rem] text-gray-600 mt-4 mb-1">
          Time Frame
        </div>
        <DateRangePicker<{
          label: string;
          value: DateRangeChoiceV2 | DateRangeChoiceV3;
        }>
          popoverPlacement="bottom-end"
          disabled={false}
          nonCustomChoices={dateRangeOptions}
          dateRange={timeFrame}
          onUpdate={(dateRange) => {
            prettyLogEventPage({
              domain: "DataExport",
              action: "onSelect",
              additionalActionInfo: "Time Frame changed",
              data: {
                newTimeFrame: dateRange,
                pageContext: { timeFrame },
              },
            });
            onTimeFrameChange(dateRange);
          }}
          maxLookbackDays={maxLookbackDays}
        />
        <h3 className="text-base font-semibold m-0 mb-3 mt-6">
          Select file name
        </h3>
        <div className="font-normal text-[0.65rem] text-gray-600 mb-1">
          File Name
        </div>
        <TextInput
          className="text-xs"
          value={fileName}
          onChange={(e) => {
            if (e.target.value.match(/^[A-Za-z0-9_/\-]*$/)) {
              onFileNameChange(e.target.value);
            }
          }}
        />
        {!fileName && (
          <div className="font-normal text-[0.65rem] text-mars mt-0.5">
            File Name is required
          </div>
        )}
        {fileName && fileName.length < 3 && (
          <div className="font-normal text-[0.65rem] text-mars mt-0.5">
            File Name must be at least 3 characters long
          </div>
        )}
      </div>
    </div>
  );
};
